import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import api from '../../config/api';
import {
  SignalIcon,
  ChartBarIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  BoltIcon,
  CloudArrowUpIcon
} from '@heroicons/react/24/outline';

const Container = styled.div`
  background: ${theme.colors.background.paper};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  padding: 1.5rem;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const Title = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.md};
  color: ${theme.colors.text.primary};
  font-size: 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${theme.colors.background.secondary};
    border-color: ${theme.colors.primary.main};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const StatusCard = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.md};
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StatusHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const StatusLabel = styled.div`
  color: ${theme.colors.text.secondary};
  font-size: 0.875rem;
  font-weight: 500;
`;

const StatusIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  background: ${props => props.color || theme.colors.primary.main};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const StatusValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const StatusSubtext = styled.div`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`;

const ConnectionStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background: ${props => {
    switch (props.status) {
      case 'active': return theme.colors.success.light;
      case 'inactive': return theme.colors.warning.light;
      case 'offline': return theme.colors.error.light;
      default: return theme.colors.text.tertiary;
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'active': return theme.colors.success.dark;
      case 'inactive': return theme.colors.warning.dark;
      case 'offline': return theme.colors.error.dark;
      default: return theme.colors.text.secondary;
    }
  }};
  border-radius: ${theme.borderRadius.md};
  font-size: 0.875rem;
  font-weight: 500;
`;

const ActivityFeed = styled.div`
  border-top: 1px solid ${theme.colors.border.secondary};
  padding-top: 1rem;
`;

const ActivityTitle = styled.div`
  color: ${theme.colors.text.primary};
  font-weight: 600;
  margin-bottom: 0.75rem;
  font-size: 0.875rem;
`;

const ActivityList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-height: 200px;
  overflow-y: auto;
`;

const ActivityItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.sm};
  font-size: 0.875rem;
`;

const ActivityTime = styled.span`
  color: ${theme.colors.text.secondary};
  font-family: monospace;
  font-size: 0.75rem;
`;

const ActivityMessage = styled.span`
  color: ${theme.colors.text.primary};
`;

const Pulse = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.color || theme.colors.success.main};
  animation: pulse 2s infinite;
  margin-right: 0.5rem;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const SignalKMonitor = ({ vesselId }) => {
  const [monitoring, setMonitoring] = useState({
    status: 'loading',
    lastPush: null,
    pushCount: 0,
    streamConnected: false,
    dataPointsToday: 0,
    avgPushRate: 0,
    recentActivity: []
  });
  const [refreshing, setRefreshing] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const formatActivityMessage = (log) => {
    const metadata = log.metadata || {};
    
    switch (log.action) {
      case 'ingest':
        return `Batch push: ${metadata.dataPointCount || 0} data points`;
      case 'rate_limited':
        return `⚠️ Rate limited (retry in ${metadata.retryAfter || 1}s)`;
      case 'regenerate':
        return `🔑 Token regenerated by user ${metadata.regeneratedBy || 'admin'}`;
      case 'view':
        return `👁️ Token viewed by user`;
      default:
        return `${log.action}: ${log.response_status || 'unknown'}`;
    }
  };

  useEffect(() => {
    loadMonitoringData();

    // Auto-refresh every 10 seconds if enabled
    const interval = setInterval(() => {
      if (autoRefresh) {
        loadMonitoringData();
      }
    }, 10000);

    return () => clearInterval(interval);
  }, [vesselId, autoRefresh]);

  const loadMonitoringData = async () => {
    try {
      // Load token/stats for this vessel
      const tokenResponse = await api.get(`/api/vessels/${vesselId}/token`, {
        params: { poll: 'true' }
      });
      const token = tokenResponse.data;

      // Load real-time WebSocket stream status
      let streamConnected = false;
      try {
        const streamResponse = await api.get(`/api/vessels/${vesselId}/token/stream-status`);
        streamConnected = streamResponse.data.stream_connected;
      } catch (error) {
        console.error('Error loading stream status:', error);
      }

      // Load audit logs for recent activity
      let recentActivity = [];
      try {
        const activityResponse = await api.get(`/api/vessels/${vesselId}/token/activity?limit=10`);
        recentActivity = activityResponse.data.activity.map(log => ({
          time: new Date(log.created_at),
          message: formatActivityMessage(log),
          type: log.action
        }));
      } catch (error) {
        console.error('Error loading activity logs:', error);
      }

      if (!token.last_used_at) {
        setMonitoring({
          status: streamConnected ? 'active' : 'offline',
          lastPush: null,
          pushCount: token.push_count || 0,
          streamConnected,
          dataPointsToday: Math.floor((token.push_count || 0) * 42),
          avgPushRate: 42,
          recentActivity
        });
        return;
      }

      const lastUsed = new Date(token.last_used_at);
      const now = new Date();
      const diffMinutes = (now - lastUsed) / (1000 * 60);
      
      let status = 'offline';
      if (streamConnected || diffMinutes < 2) status = 'active';
      else if (diffMinutes < 10) status = 'inactive';

      if (recentActivity.length === 0) {
        recentActivity = [{
          time: lastUsed,
          message: 'Data push received',
          type: 'ingest'
        }];
      }

      setMonitoring({
        status,
        lastPush: token.last_used_at,
        pushCount: token.push_count || 0,
        streamConnected: streamConnected,
        dataPointsToday: Math.floor((token.push_count || 0) * 42),
        avgPushRate: 42,
        recentActivity
      });
    } catch (error) {
      console.error('Error loading monitoring data:', error);
      setMonitoring(prev => ({
        ...prev,
        status: 'error'
      }));
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await loadMonitoringData();
    setTimeout(() => setRefreshing(false), 500);
  };

  const getStatusIcon = () => {
    switch (monitoring.status) {
      case 'active': return CheckCircleIcon;
      case 'inactive': return ExclamationTriangleIcon;
      case 'offline': return XCircleIcon;
      default: return ClockIcon;
    }
  };

  const getStatusText = () => {
    switch (monitoring.status) {
      case 'active': return 'Active - Receiving Data';
      case 'inactive': return 'Inactive - No Recent Data';
      case 'offline': return 'Offline - Not Connected';
      default: return 'Loading...';
    }
  };

  const getTimeSince = (timestamp) => {
    if (!timestamp) return 'Never';
    
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const StatusIconComponent = getStatusIcon();

  return (
    <Container>
      <Header>
        <Title>
          <ChartBarIcon className="w-5 h-5" />
          Real-Time Monitoring
        </Title>
        <RefreshButton onClick={handleRefresh} disabled={refreshing}>
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          {refreshing ? 'Refreshing...' : 'Refresh'}
        </RefreshButton>
      </Header>

      <ConnectionStatus status={monitoring.status}>
        {monitoring.status === 'active' && <Pulse color={theme.colors.success.main} />}
        <StatusIconComponent className="w-5 h-5" />
        {getStatusText()}
      </ConnectionStatus>

      <StatusGrid style={{ marginTop: '1.5rem' }}>
        <StatusCard>
          <StatusHeader>
            <StatusLabel>Last Data Push</StatusLabel>
            <StatusIcon color={theme.colors.primary.main}>
              <ClockIcon className="w-5 h-5" />
            </StatusIcon>
          </StatusHeader>
          <StatusValue style={{ fontSize: '1.5rem' }}>
            {getTimeSince(monitoring.lastPush)}
          </StatusValue>
          <StatusSubtext>
            {monitoring.lastPush 
              ? new Date(monitoring.lastPush).toLocaleString()
              : 'No pushes yet'}
          </StatusSubtext>
        </StatusCard>

        <StatusCard>
          <StatusHeader>
            <StatusLabel>Total Pushes</StatusLabel>
            <StatusIcon color={theme.colors.accent.main}>
              <CloudArrowUpIcon className="w-5 h-5" />
            </StatusIcon>
          </StatusHeader>
          <StatusValue>{monitoring.pushCount.toLocaleString()}</StatusValue>
          <StatusSubtext>Since plugin installation</StatusSubtext>
        </StatusCard>

        <StatusCard>
          <StatusHeader>
            <StatusLabel>Data Points Today</StatusLabel>
            <StatusIcon color={theme.colors.success.main}>
              <SignalIcon className="w-5 h-5" />
            </StatusIcon>
          </StatusHeader>
          <StatusValue>{monitoring.dataPointsToday.toLocaleString()}</StatusValue>
          <StatusSubtext>~{monitoring.avgPushRate} per push</StatusSubtext>
        </StatusCard>

        <StatusCard>
          <StatusHeader>
            <StatusLabel>Stream Status</StatusLabel>
            <StatusIcon color={monitoring.streamConnected ? theme.colors.secondary.main : theme.colors.text.tertiary}>
              <BoltIcon className="w-5 h-5" />
            </StatusIcon>
          </StatusHeader>
          <StatusValue style={{ fontSize: '1.25rem' }}>
            {monitoring.streamConnected ? 'Connected' : 'Disconnected'}
          </StatusValue>
          <StatusSubtext>WebSocket real-time stream</StatusSubtext>
        </StatusCard>
      </StatusGrid>

      <ActivityFeed>
        <ActivityTitle>Recent Activity</ActivityTitle>
        <ActivityList>
          {monitoring.recentActivity.length > 0 ? (
            monitoring.recentActivity.map((activity, index) => (
              <ActivityItem key={index}>
                <ActivityMessage>{activity.message}</ActivityMessage>
                <ActivityTime>{getTimeSince(activity.time)}</ActivityTime>
              </ActivityItem>
            ))
          ) : (
            <ActivityItem>
              <ActivityMessage style={{ color: theme.colors.text.secondary }}>
                No recent activity
              </ActivityMessage>
            </ActivityItem>
          )}
        </ActivityList>
      </ActivityFeed>
    </Container>
  );
};

export default SignalKMonitor;

